import { NumberField, Typography } from "@hellocarbo/lasagne";
import { useTranslation } from "react-i18next";

import { TTrajectoryStep } from "@/types";
import { TTrackingCampaign } from "@/types";
import { generateProjectionYearsToBeFilledIn } from "@/utils/trajectory";

type ProjectionAssumptionProps = {
  year: number;
  turnover?: number;
  employees?: number;
  disabled?: boolean;
};

function ProjectionAssumption({
  year,
  turnover,
  employees,
  disabled = false,
}: ProjectionAssumptionProps) {
  const { t } = useTranslation(["trajectory"]);

  return (
    <tr>
      <td className="bg-grayscale-xx-light p-xs rounded-md text-center">
        <Typography
          step="-2"
          weight="semibold"
          as="div"
          className="text-grayscale-dark"
        >
          {year}
        </Typography>
      </td>
      <td>
        <NumberField
          disabled={disabled}
          defaultValue={employees}
          name={`${year}-employees`}
          fullWidth
          size="medium"
        />
      </td>
      <td>
        <NumberField
          unit={t("trajectory:projections.table.unit")}
          disabled={disabled}
          defaultValue={turnover}
          name={`${year}-turnover`}
          fullWidth
          size="medium"
        />
      </td>
    </tr>
  );
}

// If projection year === default projection year, display the existing trajectory steps
// Otherwise generate the years to be filled in
function getAssumptionYears(
  projectionYear: number,
  defaultProjectionYear: number,
  trajectorySteps: Array<TTrajectoryStep>,
  trackingCampaignYear: number
) {
  return projectionYear === defaultProjectionYear && trajectorySteps.length
    ? trajectorySteps
        .filter(
          (trajectoryStep) => trajectoryStep.year !== trackingCampaignYear
        ) // do not display the tracking campaign year twice
        .map((trajectoryStep) => trajectoryStep.year)
    : generateProjectionYearsToBeFilledIn(
        trackingCampaignYear + 1,
        projectionYear
      );
}

type ProjectionAssumptionsProps = {
  trajectorySteps: Array<TTrajectoryStep>;
  trackingCampaign: TTrackingCampaign;
  defaultProjectionYear: number;
  projectionYear: number;
  readOnly?: boolean;
};

function ProjectionAssumptions({
  trajectorySteps,
  trackingCampaign,
  defaultProjectionYear,
  projectionYear,
  readOnly = false,
}: ProjectionAssumptionsProps) {
  const { t } = useTranslation(["trajectory"]);

  const trackingCampaignYear = new Date(
    trackingCampaign.period_start
  ).getFullYear();

  const assumptionYears = getAssumptionYears(
    projectionYear,
    defaultProjectionYear,
    trajectorySteps,
    trackingCampaignYear
  );

  return (
    <table className="mt-lg border-spacing-xs w-full table-auto border-separate">
      <thead>
        <tr>
          <th>
            <Typography step="-1" weight="semibold" className="text-grayscale">
              {t("trajectory:projections.table.year")}
            </Typography>
          </th>
          <th>
            <Typography step="-1" weight="semibold" className="text-grayscale">
              {t("trajectory:projections.table.employees")}
            </Typography>
          </th>
          <th>
            <Typography step="-1" weight="semibold" className="text-grayscale">
              {t("trajectory:projections.table.turnover")}
            </Typography>
          </th>
        </tr>
      </thead>
      <tbody className="gap-y-2xs">
        <ProjectionAssumption
          year={trackingCampaignYear}
          employees={trackingCampaign.employees}
          turnover={trackingCampaign.turnover}
          disabled
        />
        {assumptionYears.map((assumptionYear) => {
          const trajectoryStep = trajectorySteps.find(
            (trajectoryStep) => trajectoryStep.year === assumptionYear
          );

          return (
            <ProjectionAssumption
              key={`assumption-${assumptionYear}`}
              year={assumptionYear}
              employees={trajectoryStep?.employees}
              turnover={trajectoryStep?.turnover}
              disabled={readOnly}
            />
          );
        })}
      </tbody>
    </table>
  );
}

export default ProjectionAssumptions;
